// src/components/Terms.jsx
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

function Terms() {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 md:p-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Terms of Service
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
            Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>

          <div className="prose prose-gray dark:prose-invert max-w-none">
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                By accessing and using Epiphoric ("the Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to these Terms of Service, please do not use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">2. Description of Service</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Epiphoric is an AI-powered idea management application that allows users to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Create, organize, and manage ideas</li>
                <li>Track progress with status updates and ratings</li>
                <li>Attach resources and links to ideas</li>
                <li>Manage TODO lists with drag-and-drop functionality</li>
                <li>Capture lessons learned from completed projects</li>
                <li><strong>AI enhancement:</strong> Develop ideas using AI-powered assistance (Autopilot and Guided Journey), subject to your account entitlement</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3 mt-6">2.1 AI-Powered Features</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Epiphoric offers optional AI-powered features to help develop your ideas. Access is granted through <strong>entitlements</strong> (for example, a complimentary enhancement credit, a paid subscription with monthly enhancement credits, or operator-granted access for certain accounts)—not automatically for every account. Available modes include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li><strong>Autopilot Mode:</strong> Automatically generates enhanced titles, descriptions, tags, subtasks, and execution plans from your idea description</li>
                <li><strong>Guided Journey:</strong> Step-by-step AI collaboration where you review and refine AI suggestions at each stage</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                AI features utilize third-party AI services (currently OpenAI). When you use AI features, your idea content is sent to these services for processing. See our Privacy Policy for details on how this data is handled.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">3. User Accounts</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">3.1 Account Creation</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To use Epiphoric, you must create an account. You can sign up using:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Email and password</li>
                <li>Google OAuth (single sign-on)</li>
                <li>Apple Sign In (single sign-on)</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">3.2 Account Security</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You are responsible for:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Keeping your encryption key secure (if applicable)</li>
                <li>Notifying us immediately of any unauthorized use of your account</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">3.3 Account Termination</h3>
              <p className="text-gray-700 dark:text-gray-300">
                You may delete your account at any time through your profile settings. Upon account deletion, all associated data will be permanently removed from our systems.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">4. Data and Content</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.1 Your Content</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You retain all ownership rights to the content you create and store in Epiphoric. By using the Service, you grant us a limited license to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Store your encrypted data on our servers</li>
                <li>Process your data to provide the Service</li>
                <li>Back up your data for disaster recovery purposes</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.2 Data Encryption</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Sensitive data (idea titles, descriptions, status, tags, TODOs) is encrypted using AES encryption. You acknowledge that:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>We cannot decrypt your encrypted data without your encryption key</li>
                <li>If you lose your encryption key, we cannot recover your encrypted data</li>
                <li>Lessons learned and resource links are stored as plain text for searchability</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">4.3 Prohibited Content</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You agree not to use Epiphoric to store or transmit:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Illegal content or content that violates any laws</li>
                <li>Content that infringes on intellectual property rights</li>
                <li>Malicious code, viruses, or harmful software</li>
                <li>Content that violates the rights of others</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">5. AI Enhancement Access and Usage</h2>
              
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5.1 Eligibility</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                AI-powered development (<strong>Autopilot</strong> and <strong>Guided Journey</strong>) requires an active <strong>entitlement</strong> on your account. Depending on our policies and your account, entitlement may include:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>A <strong>complimentary enhancement credit</strong> (typically one per account under current policy). Using AI enhancement on <strong>Autopilot</strong> or on the final step of <strong>Guided Journey</strong> consumes that credit; you cannot use both paths on a single complimentary credit alone.</li>
                <li><strong>Paid or promotional access</strong> when we offer subscriptions or similar programs (terms will be presented when available).</li>
                <li><strong>Operator-granted or internal access</strong>, including certain legacy administrator account setups used for product operations.</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may change eligibility, credits, and pricing with notice as described in Section 12. Access may be <strong>revoked</strong> for violation of these Terms.
              </p>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5.2 AI-Generated Content</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                When using AI features, you acknowledge that:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>AI-generated content (titles, descriptions, tags, tasks, plans) is provided as suggestions only</li>
                <li>You are responsible for reviewing and editing AI output before use</li>
                <li>AI suggestions may not always be accurate, appropriate, or suitable for your needs</li>
                <li>You retain ownership of both your input and any AI-generated content you choose to save</li>
                <li>We are not liable for any decisions made based on AI-generated content</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">5.3 Acceptable Use of AI Features</h3>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                You agree not to use AI features to:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Generate illegal, harmful, or abusive content</li>
                <li>Attempt to extract or reverse-engineer the AI system</li>
                <li>Circumvent usage limits or access controls</li>
                <li>Use the service in any way that violates OpenAI's usage policies</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">6. Service Availability</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We strive to provide reliable service but do not guarantee:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Uninterrupted or error-free service</li>
                <li>That the Service will meet your specific requirements</li>
                <li>That defects will be corrected immediately</li>
                <li>Availability of AI features at all times (AI services may have outages)</li>
              </ul>
              <p className="text-gray-700 dark:text-gray-300">
                We reserve the right to modify, suspend, or discontinue the Service at any time with or without notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">7. Intellectual Property</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                The Service, including its original content, features, and functionality, is owned by Epiphoric and is protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                You may not reproduce, distribute, modify, create derivative works of, publicly display, or commercially exploit any part of the Service without our express written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                To the maximum extent permitted by law:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Epiphoric is provided "as is" and "as available" without warranties of any kind</li>
                <li>We are not liable for any indirect, incidental, special, or consequential damages</li>
                <li>We are not liable for data loss, corruption, or unauthorized access</li>
                <li>Our total liability shall not exceed the amount you paid for the Service (if any) in the past 12 months</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">9. Indemnification</h2>
              <p className="text-gray-700 dark:text-gray-300">
                You agree to indemnify and hold harmless Epiphoric, its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including legal fees) arising out of or relating to your use of the Service, violation of these Terms, or infringement of any rights of another.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">10. Termination</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including if you breach the Terms. Upon termination:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-700 dark:text-gray-300 space-y-2">
                <li>Your right to use the Service will cease immediately</li>
                <li>All data associated with your account may be deleted</li>
                <li>You may export your data before termination (contact support for assistance)</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">11. Governing Law</h2>
              <p className="text-gray-700 dark:text-gray-300">
                These Terms shall be governed by and construed in accordance with the laws of the jurisdiction in which Epiphoric operates, without regard to its conflict of law provisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">12. Changes to Terms</h2>
              <p className="text-gray-700 dark:text-gray-300">
                We reserve the right to modify or replace these Terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion. By continuing to access or use the Service after those revisions become effective, you agree to be bound by the revised terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">13. Contact Information</h2>
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                If you have any questions about these Terms of Service, please contact us through our support channel:
              </p>
              <p className="text-gray-700 dark:text-gray-300">
                <a 
                  href="https://github.com/Leftyshields/epiphoric/issues" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  GitHub Issues
                </a>
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                All support requests are handled through GitHub Issues. Please open an issue for questions about these terms.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Terms;

